import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  X as CloseIcon,
  Building2,
  User,
  Users,
  Calendar,
  DollarSign,
  FileText,
  Home,
  Phone,
  Mail,
  Tag,
  BarChart3,
  Receipt,
  Layers,
  ShoppingBag,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Save,
  ArrowLeft as BackIcon,
  CreditCard,
  Calculator
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';

const CreatePurchaseOrder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    supplier_id: '',
    expected_delivery_date: '',
    mode_of_purchase: '',
    remark: '',
    items: []
  });

  // Data state
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);



  // Fetch suppliers and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [suppliersResponse, productsResponse] = await Promise.all([
          apiService.request('/suppliers'),
          apiService.request('/products')
        ]);
        setSuppliers(suppliersResponse.data);
        setProducts(productsResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, []);

  // Update selected supplier when supplier_id changes
  useEffect(() => {
    if (formData.supplier_id) {
      const supplier = suppliers.find(s => s.id === parseInt(formData.supplier_id));
      setSelectedSupplier(supplier);
    } else {
      setSelectedSupplier(null);
    }
  }, [formData.supplier_id, suppliers]);

  // Handlers

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: '',
        quantity: '',
        cost_price: '',
        subtotal: 0
      }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'cost_price') {
            const quantity = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
            const costPrice = parseFloat(field === 'cost_price' ? value : item.cost_price) || 0;
            updatedItem.subtotal = quantity * costPrice;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => total + (item.subtotal || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      setError('Please select a supplier');
      return;
    }

    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    // Validate items
    for (let item of formData.items) {
      if (!item.product_id || !item.quantity || !item.cost_price) {
        setError('Please fill in all item details');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.request('/purchases', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/purchases');
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to create purchase order');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Layout>
      <div className="space-y-6">
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl">
              <div className="flex items-center">
                <CheckCircle size={20} className="mr-2" />
                Purchase order created successfully! Redirecting...
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl">
              <div className="flex items-center">
                <X size={20} className="mr-2" />
                {error}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Purchase Information */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <FileText size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Purchase Information</h2>
                  <p className="text-gray-600">Basic details for the purchase order</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Supplier Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Supplier *
                  </label>
                  <select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select a supplier</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Expected Delivery Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Delivery Date
                  </label>
                  <input
                    type="date"
                    name="expected_delivery_date"
                    value={formData.expected_delivery_date}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                {/* Mode of Purchase */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mode of Purchase
                  </label>
                  <select
                    name="mode_of_purchase"
                    value={formData.mode_of_purchase}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select mode</option>
                    <option value="cash">Cash</option>
                    <option value="credit">Credit</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">Mobile Money</option>
                  </select>
                </div>

                {/* Remark */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Remark
                  </label>
                  <textarea
                    name="remark"
                    value={formData.remark}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Additional notes or remarks..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Selected Supplier Info */}
              {selectedSupplier && (
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Supplier Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm font-medium text-blue-700">Contact Person</p>
                      <p className="text-blue-900">{selectedSupplier.contact_person || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-700">Phone</p>
                      <p className="text-blue-900">{selectedSupplier.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-700">Email</p>
                      <p className="text-blue-900">{selectedSupplier.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-700">Address</p>
                      <p className="text-blue-900">{selectedSupplier.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Purchase Items */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                    <Package size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Purchase Items</h2>
                    <p className="text-gray-600">Add products to the purchase order</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  <Plus size={16} />
                  Add Item
                </button>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-12">
                  <Package size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500 text-lg">No items added yet</p>
                  <p className="text-gray-400">Click "Add Item" to start adding products</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                        {/* Product Selection */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Product *
                          </label>
                          <select
                            value={item.product_id}
                            onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                          >
                            <option value="">Select product</option>
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} - {product.sku}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Quantity */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Quantity *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            required
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                          />
                        </div>

                        {/* Cost Price */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Cost Price *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={item.cost_price}
                            onChange={(e) => updateItem(index, 'cost_price', e.target.value)}
                            required
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                          />
                        </div>

                        {/* Subtotal & Actions */}
                        <div className="flex items-end space-x-2">
                          <div className="flex-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Subtotal
                            </label>
                            <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium">
                              {formatCurrency(item.subtotal || 0)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-200"
                            title="Remove item"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Total */}
              {formData.items.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-purple-900">Total Cost:</span>
                    <span className="text-2xl font-bold text-purple-900">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/purchases')}
                className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || formData.items.length === 0}
                className="px-6 py-3 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-xl hover:from-red-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Create Purchase Order
                  </>
                )}
              </button>
            </div>
          </form>
      </div>
    </Layout>
  );
};

export default CreatePurchaseOrder;
